import { useState } from 'react';

import { useEvent } from '@mint-lib/hooks';
import { useTranslation } from '@mint-lib/i18n';
import { useQuery } from '@mint-lib/query-client';
import {
  Button,
  CircularLoadingButton,
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogOverlay,
  DialogPortal,
  DialogRoot,
  DialogTitle,
} from '@mint-lib/tw-components';

import {
  getFileAccessOption,
  useUpdateFileAccessMutation,
} from '../../../queries/fileAccess.js';
import type {
  FileAccess,
  FileAccessType,
  FileAccessUser,
  RagTableType,
} from '../../../types/rag.js';
import { ShareFilesSection } from './ShareFilesSection.jsx';

type FieldsProps = {
  fileId: string;
  initialAccess: FileAccess;
  onClose: () => void;
};

const EditPermissionsFields = ({
  fileId,
  initialAccess,
  onClose,
}: FieldsProps) => {
  const { t } = useTranslation('@mint-lib/ai-chat');
  const { mutate: updateFileAccess, isPending } = useUpdateFileAccessMutation();

  const [accessType, setAccessType] = useState<FileAccessType>(
    initialAccess.accessType,
  );
  const [sharedUsers, setSharedUsers] = useState<FileAccessUser[]>(
    initialAccess.users,
  );

  const handleSave = useEvent(() => {
    updateFileAccess(
      { fileId, access: { accessType, users: sharedUsers } },
      { onSuccess: onClose },
    );
  });

  return (
    <>
      <DialogContent className="flex min-h-0 flex-col overflow-y-auto">
        <ShareFilesSection
          accessType={accessType}
          onAccessTypeChange={setAccessType}
          sharedUsers={sharedUsers}
          onSharedUsersChange={setSharedUsers}
        />
      </DialogContent>
      <DialogFooter>
        <Button size="large" variant="plain" onClick={onClose}>
          {t('Cancel')}
        </Button>
        <Button size="large" onClick={handleSave} disabled={isPending}>
          {isPending && (
            <CircularLoadingButton size="small" variant="outlined" />
          )}
          {t('Save')}
        </Button>
      </DialogFooter>
    </>
  );
};

type Props = {
  isOpen: boolean;
  onClose: () => void;
  document: RagTableType | null;
};

const EditPermissionsDialog = ({ isOpen, onClose, document }: Props) => {
  const { t } = useTranslation('@mint-lib/ai-chat');
  const fileId = document?.fileId;

  const { data: fileAccess } = useQuery({
    ...getFileAccessOption(fileId ?? ''),
    enabled: isOpen && Boolean(fileId),
  });

  if (!fileId) return null;

  return (
    <Dialog modal onOpenChange={onClose} open={isOpen}>
      <DialogPortal>
        <DialogOverlay />
        <DialogRoot
          aria-describedby={undefined}
          size="s"
          className="max-h-[90vh]"
        >
          <DialogHeader>
            <DialogTitle>{t('Edit permissions')}</DialogTitle>
          </DialogHeader>
          <EditPermissionsFields
            key={fileId}
            fileId={fileId}
            initialAccess={fileAccess}
            onClose={onClose}
          />
        </DialogRoot>
      </DialogPortal>
    </Dialog>
  );
};

export { EditPermissionsDialog };
